import React from 'react';
import KoobButton from '@koob/ui/src/components/KoobButton';

/**
 * KD-3581 §7 — the temporary band offering to undo an experience removal.
 *
 * The countdown (~6 s) and the snapshot both belong to the app: the frame this
 * band sits in disappears with the experience it describes, so the state has to
 * live above it. This component only renders what the app decides to show, and
 * announces it politely so a screen reader picks the offer up before it expires.
 *
 * It takes the place of the frame header — the band that carried the removal
 * cross — so it borrows its geometry: a white card on the tinted frame, a 34px
 * leading square, and the action on the right where the cross used to be. The
 * offer only lasts six seconds, so it has to be read at a glance.
 */
type ExperienceRemovedNoticeProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className'
> & {
  message: React.ReactNode;
  /** What the undo brings back, one line under the message. */
  description?: React.ReactNode;
  actionLabel: React.ReactNode;
  onUndo?: React.MouseEventHandler<HTMLButtonElement>;
};

const ExperienceRemovedNotice = React.forwardRef<
  HTMLDivElement,
  ExperienceRemovedNoticeProps
>(({ message, description, actionLabel, onUndo, ...props }, ref) => (
  <div
    ref={ref}
    role="status"
    aria-live="polite"
    className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5 rounded-xl border border-solid border-orange-200 bg-white px-4 py-3.5"
    {...props}
  >
    <span
      aria-hidden="true"
      className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] bg-orange-50 text-[15px] text-orange-500"
    >
      <i className="fa-light fa-trash-can" />
    </span>

    <div className="min-w-[9rem] flex-1">
      <div className="text-[15px] font-bold leading-[1.3] tracking-[-0.01em] text-[#16284F]">
        {message}
      </div>

      {description && (
        <div className="mt-[3px] text-[13.5px] leading-[1.35] text-slate-500">
          {description}
        </div>
      )}
    </div>

    {onUndo && (
      <span className="ml-auto shrink-0">
        <KoobButton variant="primary" onClick={onUndo}>
          <i className="fa-light fa-rotate-left" />
          {actionLabel}
        </KoobButton>
      </span>
    )}
  </div>
));

ExperienceRemovedNotice.displayName = 'ExperienceRemovedNotice';

export default ExperienceRemovedNotice;
